import { AnnotatedType, PrimaryAnnotatedType } from '@/components/annotated-type';
import { HorizontalBarChart } from '@/components/bar-chart/horizontal';
import { BarChartItem } from '@/components/bar-chart/vertical';
import {
  OnStepEnterExitArgs,
  ScrollGraphic,
  ScrollRoot,
  ScrollStep,
  ScrollSteps,
} from '@/components/microstory/scroll';
import { StoryText } from '@/components/microstory/text';
import { useNumberFormatting } from '@/utils/use-number-formatting';
import { I18n } from '@lingui/core';
import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useCallback, useState } from 'react';

function getLabel(i18n: I18n, key: keyof Places) {
  switch (key) {
    case 'city':
      return t(i18n)({
        message: 'Their city or region',
        id: 'where-you-call-home-2023.result-city',
      });
    case 'country':
      return t(i18n)({ message: 'Their country', id: 'where-you-call-home-2023.result-country' });
    case 'world':
      return t(i18n)({ message: 'The world', id: 'where-you-call-home-2023.result-world' });
  }
}

export function WhereYouCallHome2023Chapter2({
  data,
}: {
  data: WhereYouCallHomeData['younger_identify_as'];
}) {
  const { i18n } = useLingui();
  const formatPercentage = useNumberFormatting('percent');

  const [chartData, setChartData] = useState<BarChartItem[]>([]);
  const [chartVisible, setChartVisible] = useState(false);

  const handleStepEnter = useCallback((e: OnStepEnterExitArgs<BarChartItem[]>) => {
    const stepData = e.data;
    if (stepData) {
      setChartVisible(true);
      setChartData(stepData);
    }
  }, []);

  return (
    <ScrollRoot>
      <ScrollGraphic>
        <HorizontalBarChart
          label={t({
            id: 'where-you-call-home-2023.chapter-2.chart-label',
            message: '% of young people who say they identify most with',
          })}
          data={chartData}
          visible={chartVisible}
          formatValue={formatPercentage}
        />
      </ScrollGraphic>

      <ScrollSteps onStepEnter={handleStepEnter} offset={0.7} hasGraphic>
        <ScrollStep
          data={[
            { label: getLabel(i18n, 'world'), value: data.world },
            { label: getLabel(i18n, 'city'), value: data.city },
          ]}
        >
          <StoryText>
            <Trans id="where-you-call-home-2023.chapter-2.text-1">
              We found that on average, about equal numbers of{' '}
              <PrimaryAnnotatedType>young people</PrimaryAnnotatedType> say they feel most at home
              in <AnnotatedType>the world</AnnotatedType> ({formatPercentage(data.world)}) or in{' '}
              <AnnotatedType>their city or region</AnnotatedType> ({formatPercentage(data.city)}).
            </Trans>
          </StoryText>
        </ScrollStep>

        <ScrollStep
          data={[
            { label: getLabel(i18n, 'country'), value: data.country },
            { label: getLabel(i18n, 'world'), value: data.world },
            { label: getLabel(i18n, 'city'), value: data.city },
          ]}
        >
          <StoryText>
            <Trans id="where-you-call-home-2023.chapter-2.text-2">
              Meanwhile {formatPercentage(data.country)} say their greatest affinity is to{' '}
              <AnnotatedType>their country</AnnotatedType>.
            </Trans>
          </StoryText>
        </ScrollStep>
      </ScrollSteps>
    </ScrollRoot>
  );
}
